import { Injectable } from '@angular/core';
import { Firestore, doc, updateDoc, addDoc, collection, serverTimestamp } from '@angular/fire/firestore';
import { ToastrService } from 'ngx-toastr';
import { Pig } from '../interfaces/pig.interface';

export interface PaymentRequest {
  orderId: string;
  amount: number;
  description: string;
  returnUrl: string;
  cancelUrl: string;
}

export interface PaymentResponse {
  success: boolean;
  paymentUrl?: string;
  transactionId?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  
  constructor(
    private firestore: Firestore,
    private toastr: ToastrService
  ) {}

  /**
   * Tạo yêu cầu thanh toán VNPay
   */
  async createVNPayPayment(order: Pig): Promise<PaymentResponse> {
    try {
      const paymentRequest: PaymentRequest = {
        orderId: order.id || Date.now().toString(),
        amount: order.purchasedPrice,
        description: `Thanh toán đơn hàng ${order.pigNumber} - ${order.breed}`,
        returnUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/payment-cancel`
      };

      // Gọi API backend để tạo URL thanh toán VNPay
      const response = await fetch('/api/payment/vnpay/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentRequest)
      });

      const result = await response.json();
      
      if (result.success && result.paymentUrl) {
        // Lưu thông tin giao dịch vào Firestore
        await this.savePaymentTransaction(order, result.transactionId);
        return result;
      } else {
        throw new Error(result.message || 'Không thể tạo yêu cầu thanh toán');
      }
    } catch (error) {
      console.error('Error creating VNPay payment:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi tạo yêu cầu thanh toán'
      };
    }
  }

  /**
   * Tạo yêu cầu thanh toán MoMo
   */
  async createMoMoPayment(order: Pig): Promise<PaymentResponse> {
    try {
      const paymentRequest = {
        orderId: order.id || Date.now().toString(),
        amount: order.purchasedPrice,
        orderInfo: `Thanh toán đơn hàng ${order.pigNumber}`,
        redirectUrl: `${window.location.origin}/payment-success`,
        ipnUrl: `${window.location.origin}/api/payment/momo/ipn`,
        extraData: JSON.stringify({ pigId: order.id })
      };

      const response = await fetch('/api/payment/momo/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentRequest)
      });

      const result = await response.json();
      
      if (result.success && result.payUrl) {
        await this.savePaymentTransaction(order, result.orderId);
        return {
          success: true,
          paymentUrl: result.payUrl,
          transactionId: result.orderId
        };
      } else {
        throw new Error(result.message || 'Không thể tạo yêu cầu thanh toán MoMo');
      }
    } catch (error) {
      console.error('Error creating MoMo payment:', error);
      return {
        success: false,
        message: 'Có lỗi xảy ra khi tạo yêu cầu thanh toán MoMo'
      };
    }
  }

  /**
   * Lưu thông tin giao dịch vào Firestore
   */
  private async savePaymentTransaction(order: Pig, transactionId: string): Promise<void> {
    try {
      await addDoc(collection(this.firestore, 'payments'), {
        orderId: order.id,
        transactionId: transactionId,
        pigNumber: order.pigNumber,
        amount: order.purchasedPrice,
        status: 'pending',
        createdAt: serverTimestamp(),
        orderDetails: {
          breed: order.breed,
          weight: order.weight,
          unit: order.unit
        }
      });
    } catch (error) {
      console.error('Error saving payment transaction:', error);
    }
  }

  /**
   * Cập nhật trạng thái thanh toán
   */
  async updatePaymentStatus(transactionId: string, status: 'success' | 'failed' | 'cancelled'): Promise<void> {
    try {
      // Tìm và cập nhật document payment
      const paymentRef = doc(this.firestore, 'payments', transactionId);
      await updateDoc(paymentRef, {
        status: status,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
  }
}